import React, { useCallback, useEffect, useRef, useState } from 'react';
import { commandId } from './CkOperationsShared';
import { RefreshCw } from 'lucide-react';
import { loadExchangeRetry, persistExchangeRetry, clearExchangeRetry } from './workforceExchangeQueue';
import {
  Messages, asList, capabilitiesAllow, errorMessage, scopeKey, useWfRuntime, useWfScope,
} from './WorkforceShared';
import { RollupSummary, RotaShifts, fmtDay } from './WorkforceViews';
import './Workforce.css';

const TABS = [
  ['rollup', 'Week rollup'],
  ['segments', 'Segments'],
  ['rotas', 'Native rotas'],
  ['exchanges', 'Swap and offer requests'],
];

function mondayIso(d = new Date()) {
  const dt = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = dt.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setUTCDate(dt.getUTCDate() + diff);
  return dt.toISOString().replace(/\.\d{3}Z$/, '+00:00');
}

export default function Workforce() {
  const scope = useWfScope();
  return <WorkforceWorkspace key={scopeKey(scope)} scope={scope} />;
}

export function WorkforceWorkspace({ scope }) {
  const { active, request, run, busy, error, notice, setError, setNotice } = useWfRuntime(scope);
  const [caps, setCaps] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blocked, setBlocked] = useState('');
  const [tab, setTab] = useState('rollup');
  const [weekStart, setWeekStart] = useState(mondayIso);
  const [rollup, setRollup] = useState(null);
  const [segments, setSegments] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [openRota, setOpenRota] = useState(null);
  const [swaps, setSwaps] = useState([]);
  const [offers, setOffers] = useState([]);
  const [myEmployeeId, setMyEmployeeId] = useState('');
  const [myShiftOptions, setMyShiftOptions] = useState([]);
  const [otherShiftOptions, setOtherShiftOptions] = useState([]);
  const [fromShiftId, setFromShiftId] = useState('');
  const [toShiftId, setToShiftId] = useState('');
  const [offerShiftId, setOfferShiftId] = useState('');
  const [offerRecipientId, setOfferRecipientId] = useState('');
  const [listLoading, setListLoading] = useState(false);
  const [pendingExchange, setPendingExchange] = useState(null);
  const [pendingDecision, setPendingDecision] = useState(null);
  const exchangeCommands = useRef(new Map());
  const exchangeBody = (kind, body) => {
    const key = JSON.stringify([kind, body]);
    if (!exchangeCommands.current.has(key)) exchangeCommands.current.set(key, commandId());
    return { ...body, command_id: exchangeCommands.current.get(key) };
  };
  const canWrite = Boolean(caps?.can_write);
  const hasSite = Boolean(scope.locationId);

  const loadCaps = useCallback(async () => {
    setLoading(true); setBlocked(''); setError('');
    try {
      if (!scope.orgId || !scope.token) throw new Error('Sign in and select an organization to use Workforce.');
      const c = await request('get', '/capabilities');
      if (!active()) return;
      if (!capabilitiesAllow(c)) {
        setCaps(null);
        setBlocked('Workforce is not enabled for this organization, or you do not have access.');
        return;
      }
      setCaps(c);
    } catch (e) {
      if (active()) { setCaps(null); setBlocked(errorMessage(e)); }
    } finally {
      if (active()) setLoading(false);
    }
  }, [scope, request, active, setError]);
  useEffect(() => { loadCaps(); }, [loadCaps]);

  const loadRollup = useCallback(async () => {
    if (!hasSite) { setRollup(null); return; }
    setListLoading(true);
    try {
      const data = await request('get', '/rollup/week', { params: { location_id: scope.locationId, week_start: weekStart } });
      if (active()) setRollup(data);
    } catch (e) {
      if (active()) { setRollup(null); setError(errorMessage(e)); }
    } finally {
      if (active()) setListLoading(false);
    }
  }, [request, active, hasSite, scope.locationId, weekStart, setError]);

  const loadSegments = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await request('get', '/segments', {
        params: hasSite ? { location_id: scope.locationId } : undefined,
      });
      if (active()) setSegments(asList(data, 'segments'));
    } catch (e) {
      if (active()) { setSegments([]); setError(errorMessage(e)); }
    } finally {
      if (active()) setListLoading(false);
    }
  }, [request, active, hasSite, scope.locationId, setError]);

  const loadRotas = useCallback(async () => {
    setListLoading(true);
    try {
      const data = await request('get', '/native/rotas', {
        params: hasSite ? { location_id: scope.locationId } : undefined,
      });
      if (active()) setRotas(asList(data, 'rotas'));
    } catch (e) {
      if (active()) { setRotas([]); setError(errorMessage(e)); }
    } finally {
      if (active()) setListLoading(false);
    }
  }, [request, active, hasSite, scope.locationId, setError]);

  const loadExchanges = useCallback(async () => {
    setListLoading(true);
    try {
      const [swapData, offerData] = await Promise.all([
        request('get', '/native/swaps'),
        request('get', '/native/offers'),
      ]);
      if (active()) {
        setSwaps(asList(swapData, 'swaps'));
        setOffers(asList(offerData, 'offers'));
      }
    } catch (e) {
      if (active()) { setSwaps([]); setOffers([]); setError(errorMessage(e)); }
    } finally {
      if (active()) setListLoading(false);
    }
  }, [request, active, setError]);

  const loadSwapOptions = useCallback(async () => {
    try {
      const data = await request('get', '/native/my-shift-options');
      if (active()) {
        const mine = asList(data, 'my_shifts');
        const others = asList(data, 'other_shifts');
        setMyEmployeeId(data.employee_id || '');
        setMyShiftOptions(mine);
        setOtherShiftOptions(others);
        setFromShiftId((current) => mine.some((shift) => shift.shift_id === current) ? current : '');
        setToShiftId((current) => others.some((shift) => shift.shift_id === current) ? current : '');
        setOfferShiftId((current) => mine.some((shift) => shift.shift_id === current) ? current : '');
        const recipients = [...new Set(others.map((shift) => shift.employee_id))];
        setOfferRecipientId((current) => recipients.includes(current) ? current : '');
      }
    } catch (e) {
      if (active()) {
        setMyEmployeeId(''); setMyShiftOptions([]); setOtherShiftOptions([]);
        setError(errorMessage(e));
      }
    }
  }, [request, active, setError]);

  useEffect(() => {
    if (!caps) return;
    if (tab === 'rollup') loadRollup();
    else if (tab === 'segments') loadSegments();
    else if (tab === 'rotas') loadRotas();
    else if (tab === 'exchanges') {
      if (hasSite) {
        loadExchanges();
        if (!canWrite) loadSwapOptions();
        else { setMyShiftOptions([]); setOtherShiftOptions([]); }
      } else {
        setSwaps([]); setOffers([]); setMyShiftOptions([]); setOtherShiftOptions([]);
      }
    }
    else loadRotas();
  }, [caps, canWrite, tab, hasSite, loadRollup, loadSegments, loadRotas, loadExchanges, loadSwapOptions]);

  const viewRota = async (rotaId) => {
    if (openRota?.rota_id === rotaId) { setOpenRota(null); return; }
    try {
      const data = await request('get', `/native/rotas/${encodeURIComponent(rotaId)}`);
      if (active()) setOpenRota(data);
    } catch (e) {
      if (active()) setError(errorMessage(e));
    }
  };

  const publishRota = async (rotaId) => {
    await run(async () => {
      await request('post', `/native/rotas/${encodeURIComponent(rotaId)}/publish`);
      await loadRotas();
      if (openRota?.rota_id === rotaId) setOpenRota({ ...openRota, status: 'published' });
    }, 'Rota published.');
  };

  const decideExchange = async (kind, id, decision) => {
    if (pendingDecision) return;
    await run(async () => {
      setPendingDecision({kind, id});
      try {
        const result = await request('post', `/native/${kind}/${encodeURIComponent(id)}/decide`, { body: { decision } });
        if (!active()) return;
        if (!recordedDecision(result, kind, id) || result.status !== (decision === 'approve' ? 'approved' : 'rejected'))
          throw new Error('Unmatched decision acknowledgement');
        setPendingDecision(null);
        updateExchange(kind, result);
      } catch (e) {
        throw new Error(`Decision outcome is unconfirmed. Check recorded status before deciding again. ${errorMessage(e)}`);
      }
      await loadExchanges();
    }, `Request ${decision === 'approve' ? 'approved' : 'rejected'}.`);
  };

  const recordedDecision = (item, kind, id) => item
    && item[kind === 'swaps' ? 'swap_id' : 'offer_id'] === id
    && item.organization_id === scope.orgId && item.location_id === scope.locationId
    && ['approved', 'rejected'].includes(item.status)
    && typeof item.decided_by === 'string' && item.decided_by.length > 0
    && typeof item.decided_at === 'string' && Number.isFinite(Date.parse(item.decided_at));

  const updateExchange = (kind, item) => {
    const field = kind === 'swaps' ? 'swap_id' : 'offer_id';
    const setter = kind === 'swaps' ? setSwaps : setOffers;
    setter(items => items.map(existing => existing[field] === item[field] ? item : existing));
  };

  const checkDecision = async () => {
    if (!pendingDecision) return;
    const {kind, id} = pendingDecision;
    const checked = await run(async () => {
      const data = await request('get', `/native/${kind}`);
      if (!active()) return null;
      const matches = asList(data, kind).filter(item => item[kind === 'swaps' ? 'swap_id' : 'offer_id'] === id);
      const item = matches.length === 1 ? matches[0] : null;
      if (!item || item.organization_id !== scope.orgId || item.location_id !== scope.locationId
        || !(item.status === 'pending' || recordedDecision(item, kind, id)))
        throw new Error('Decision outcome remains unconfirmed; no matching authorized record.');
      updateExchange(kind, item);
      setPendingDecision(null);
      return item.status;
    });
    if (checked?.ok && checked.result && active()) setNotice(`Recorded request status: ${checked.result}.`);
  };

  const requestSwap = async () => {
    const from = myShiftOptions.find((shift) => shift.shift_id === fromShiftId);
    const to = otherShiftOptions.find((shift) => shift.shift_id === toShiftId);
    if (!from || !to || !scope.locationId) return;
    await run(async () => {
      await submitExchange('swap', exchangeBody('swap', {
        location_id: scope.locationId,
        from_employee_id: myEmployeeId,
        to_employee_id: to.employee_id,
        from_shift_id: from.shift_id,
        to_shift_id: to.shift_id,
      }));
      await Promise.all([loadExchanges(), loadSwapOptions()]);
    }, 'Swap request submitted for manager review.');
  };

  const requestOffer = async () => {
    if (!scope.locationId || !myEmployeeId || !offerShiftId || !offerRecipientId) return;
    await run(async () => {
      await submitExchange('offer', exchangeBody('offer', {
        location_id: scope.locationId,
        from_employee_id: myEmployeeId,
        to_employee_id: offerRecipientId,
        shift_id: offerShiftId,
      }));
      await Promise.all([loadExchanges(), loadSwapOptions()]);
    }, 'Shift offer submitted for manager review.');
  };

  const submitExchange = async (kind, body) => {
    const pending = await persistExchangeRetry(scope, kind, body);
    if (!active()) return;
    setPendingExchange(pending);
    const result = await request('post', `/native/${kind}s`, { body });
    if (!active()) return;
    if (Object.entries(body).some(([field, value]) => result?.[field] !== value)
        || typeof result?.[`${kind}_id`] !== 'string') throw new Error('Request acknowledgement unconfirmed');
    clearExchangeRetry(pending.key);
    setPendingExchange(null);
  };
  const restoreExchange = () => run(async () => {
    const pending = await loadExchangeRetry(scope);
    if (active()) setPendingExchange(pending);
  });
  const retryExchange = () => run(async () => {
    if (!pendingExchange) return;
    await submitExchange(pendingExchange.kind, pendingExchange.body);
    await loadExchanges();
  }, 'Request recorded.');

  if (loading) return <div className="wf-ops" data-testid="workforce-loading">Loading workforce…</div>;
  if (blocked || !caps) {
    return (
      <div className="wf-ops wf-blocked" data-testid="workforce-blocked">
        <p>{blocked || 'Workforce is not enabled for this organization, or you do not have access.'}</p>
      </div>
    );
  }

  return (
    <div className="wf-ops" data-testid="workforce-page">
      <header>
        <div>
          <h1>Workforce</h1>
          <p className="wf-muted">Labour rollup, segments, and native rota publish.</p>
        </div>
        <div className="wf-actions">
          <button type="button" onClick={loadCaps} disabled={busy}>Refresh</button>
        </div>
      </header>
      <Messages error={error} notice={notice} />
      <div className="wf-tabs" role="tablist" aria-label="Workforce tabs">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            aria-label={label}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'rollup' && (
        <section data-testid="workforce-rollup">
          <h2>Week rollup</h2>
          {!hasSite && <p className="wf-empty">Select a site to view the week rollup.</p>}
          {hasSite && (
            <>
              <label className="wf-muted">
                Week start
                <input
                  aria-label="Week start"
                  type="text"
                  value={weekStart}
                  onChange={(e) => setWeekStart(e.target.value)}
                  style={{ display: 'block', marginTop: 6, minHeight: 44, minWidth: 280 }}
                />
              </label>
              <button type="button" className="wf-primary" style={{ marginTop: 12 }} onClick={loadRollup} disabled={listLoading || busy}>
                Load rollup
              </button>
              {listLoading && <p className="wf-muted">Loading…</p>}
              {rollup && <RollupSummary rollup={rollup} />}
            </>
          )}
        </section>
      )}

      {tab === 'segments' && (
        <section data-testid="workforce-segments">
          <h2>Work segments</h2>
          {listLoading && <p className="wf-muted">Loading…</p>}
          {!listLoading && segments.length === 0 && <p className="wf-empty">No segments yet.</p>}
          {segments.length > 0 && (
            <div className="wf-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Section</th>
                    <th>Minutes</th>
                    <th>Source</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {segments.map((s) => (
                    <tr key={s.segment_id || s.external_id || `${s.employee_id}-${s.start}`}>
                      <td>{s.employee_id}</td>
                      <td>{s.section_id || '—'}</td>
                      <td>{s.minutes}</td>
                      <td>{s.source}</td>
                      <td>{s.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'rotas' && (
        <section data-testid="workforce-rotas">
          <h2>Native rotas</h2>
          {listLoading && <p className="wf-muted">Loading…</p>}
          {!listLoading && rotas.length === 0 && <p className="wf-empty">No rotas yet.</p>}
          {rotas.length > 0 && (
            <div className="wf-table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Week start</th>
                    <th>Status</th>
                    <th>Location</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rotas.map((r) => (
                    <tr key={r.rota_id}>
                      <td>{fmtDay(r.week_start)}</td>
                      <td>{r.status}</td>
                      <td>{r.location_name || r.location_id}</td>
                      <td>
                        <button
                          type="button"
                          aria-label={`View shifts for rota ${r.rota_id}`}
                          aria-expanded={openRota?.rota_id === r.rota_id}
                          disabled={busy}
                          onClick={() => viewRota(r.rota_id)}
                        >
                          {openRota?.rota_id === r.rota_id ? 'Hide shifts' : 'View shifts'}
                        </button>{' '}
                        {r.status === 'draft' && canWrite && (
                          <button
                            type="button"
                            className="wf-primary"
                            aria-label={`Publish rota ${r.rota_id}`}
                            disabled={busy}
                            onClick={() => publishRota(r.rota_id)}
                          >
                            Publish
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {openRota && <RotaShifts rota={openRota} />}
        </section>
      )}

      {tab === 'exchanges' && (
        <section data-testid="workforce-exchanges">
          <h2>Swap and offer requests</h2>
          {pendingDecision && <div role="status">
            <p>Unconfirmed decision: {pendingDecision.id}</p>
            <button type="button" disabled={busy} onClick={checkDecision}><RefreshCw size={16} aria-hidden="true"/>Check recorded decision</button>
          </div>}
          <button type="button" disabled={busy || !hasSite} onClick={restoreExchange}><RefreshCw size={16} aria-hidden="true"/>Load pending request</button>
          {pendingExchange && <div role="status">
            <p>Unconfirmed {pendingExchange.kind} request: {pendingExchange.body.command_id}</p>
            <button type="button" disabled={busy} onClick={retryExchange}><RefreshCw size={16} aria-hidden="true"/>Retry same request</button>
          </div>}
          <p className="wf-muted">Requests for the active site only. Approval changes shift assignments; review each request before deciding.</p>
          {!canWrite && <div className="wf-swap-form">
            <h3>Request a shift swap</h3>
            {!hasSite && <p className="wf-empty">Select a site to request a swap.</p>}
            {hasSite && myShiftOptions.length === 0 && <p className="wf-empty">No shifts on published rotas are linked to your employee account.</p>}
            {hasSite && myShiftOptions.length > 0 && otherShiftOptions.length === 0 && <p className="wf-empty">No other published shifts are available to swap.</p>}
            {hasSite && myShiftOptions.length > 0 && otherShiftOptions.length > 0 && <div className="wf-swap-controls">
              <label>Your shift
                <select aria-label="Your shift to swap" value={fromShiftId} onChange={(e) => setFromShiftId(e.target.value)}>
                  <option value="">Choose shift</option>
                  {myShiftOptions.map((shift) => <option key={shift.shift_id} value={shift.shift_id}>{fmtDay(shift.start)} {shift.start}–{shift.end}</option>)}
                </select>
              </label>
              <label>Requested shift
                <select aria-label="Requested shift" value={toShiftId} onChange={(e) => setToShiftId(e.target.value)}>
                  <option value="">Choose shift</option>
                  {otherShiftOptions.map((shift) => <option key={shift.shift_id} value={shift.shift_id}>{shift.employee_id} · {fmtDay(shift.start)} {shift.start}–{shift.end}</option>)}
                </select>
              </label>
              <button type="button" className="wf-primary" disabled={busy || !fromShiftId || !toShiftId} onClick={requestSwap}>Submit request</button>
            </div>}
            {hasSite && myShiftOptions.length > 0 && otherShiftOptions.length > 0 && <div className="wf-swap-controls wf-offer-controls">
              <label>Shift to offer
                <select aria-label="Shift to offer" value={offerShiftId} onChange={(e) => setOfferShiftId(e.target.value)}>
                  <option value="">Choose shift</option>
                  {myShiftOptions.map((shift) => <option key={shift.shift_id} value={shift.shift_id}>{fmtDay(shift.start)} {shift.start}–{shift.end}</option>)}
                </select>
              </label>
              <label>Offer to
                <select aria-label="Offer recipient" value={offerRecipientId} onChange={(e) => setOfferRecipientId(e.target.value)}>
                  <option value="">Choose colleague</option>
                  {[...new Set(otherShiftOptions.map((shift) => shift.employee_id))].map((employeeId) => <option key={employeeId} value={employeeId}>{employeeId}</option>)}
                </select>
              </label>
              <button type="button" disabled={busy || !offerShiftId || !offerRecipientId} onClick={requestOffer}>Submit offer</button>
            </div>}
          </div>}
          {listLoading && <p className="wf-muted">Loading…</p>}
          {!hasSite && <p className="wf-empty">Select a site to review requests.</p>}
          {hasSite && !listLoading && swaps.length === 0 && offers.length === 0 && <p className="wf-empty">No requests for this site.</p>}
          {swaps.map((item) => (
            <article key={item.swap_id} className="wf-exchange" data-testid={`swap-${item.swap_id}`}>
              <h3>Shift swap · {item.status}</h3>
              <p>{item.from_employee_id} ({item.from_shift_id}) ↔ {item.to_employee_id} ({item.to_shift_id})</p>
              {item.status === 'pending' && canWrite && <div className="wf-actions">
                <button type="button" className="wf-primary" disabled={busy || Boolean(pendingDecision)} onClick={() => decideExchange('swaps', item.swap_id, 'approve')}>Approve swap</button>
                <button type="button" disabled={busy || Boolean(pendingDecision)} onClick={() => decideExchange('swaps', item.swap_id, 'reject')}>Reject</button>
              </div>}
            </article>
          ))}
          {offers.map((item) => (
            <article key={item.offer_id} className="wf-exchange" data-testid={`offer-${item.offer_id}`}>
              <h3>Shift offer · {item.status}</h3>
              <p>{item.from_employee_id} offers {item.shift_id}{item.to_employee_id ? ` to ${item.to_employee_id}` : ' to the team'}.</p>
              {item.status === 'pending' && canWrite && <div className="wf-actions">
                <button type="button" className="wf-primary" disabled={busy || Boolean(pendingDecision)} onClick={() => decideExchange('offers', item.offer_id, 'approve')}>Approve offer</button>
                <button type="button" disabled={busy || Boolean(pendingDecision)} onClick={() => decideExchange('offers', item.offer_id, 'reject')}>Reject</button>
              </div>}
            </article>
          ))}
        </section>
      )}
    </div>
  );
}
